import { useLanguage } from "@/context/LanguageContext";
import { getPragueIsoWeekNumber } from "@/lib/date";
import type { PublicWatchDogOffer } from "@/types";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import { useState } from "react";

type RankedEstateIntroSlideProps = {
  offer: PublicWatchDogOffer;
  estateType: string;
  offerType?: "sale" | "rent";
  questionIndex?: number;
};

export function RankedEstateIntroSlide({
  offer,
  estateType,
  offerType = "sale",
  questionIndex,
}: RankedEstateIntroSlideProps) {
  const { language } = useLanguage();

  const fallbackImage = "/image/membership/star4.png";

  const [imgSrc, setImgSrc] = useState(
    offer.mainPicUrl?.includes("empty.gif")
      ? fallbackImage
      : offer.mainPicUrl || fallbackImage,
  );

  const questionPool = {
    cs: [
      "Chtěli byste bydlet v #1?",
      "Chtěli byste tady bydlet?",
      "Je tohle váš vysněný domov?",
      "Vybrali byste si tuto nemovitost?",
      "Dokázali byste si to tady představit?",
      "Který byste si vybrali vy?",
    ],
    en: [
      "Would you like to live in #1?",
      "Would you live here?",
      "Is this your dream home?",
      "Would you choose this property?",
      "Can you picture yourself living here?",
      "Which one would you pick?",
    ],
  };

  const selectedQuestionIndex = questionIndex ?? 0;
  const weekNumber = getPragueIsoWeekNumber();
  const question =
    questionPool[language][
      selectedQuestionIndex % questionPool[language].length
    ];
  const isRent = offerType === "rent";

  const titles = {
    cs: {
      Byty: "Nejdražší byty v Česku",
      Domy: "Nejdražší domy v Česku",
      fallback: "Nejdražší nemovitosti v Česku",
      rentQualifier: "k pronájmu",
      note: "za poslední týden",
      week: `TÝDEN ${weekNumber}`,
      prompt: "Prohlédnout",
    },
    en: {
      Byty: "The most expensive apartments in Czechia",
      Domy: "The most expensive houses in Czechia",
      fallback: "The most expensive properties in Czechia",
      rentQualifier: "for rent",
      note: "for the last week",
      week: `Week ${weekNumber} of the year`,
      prompt: "Explore",
    },
  }[language];

  const title =
    titles[estateType as keyof Pick<typeof titles, "Byty" | "Domy">] ??
    titles.fallback;

  return (
    <div
      className={`relative size-full overflow-hidden rounded-2xl shadow-lg ${
        isRent ? "bg-black dark:bg-softiq-secondary" : "bg-softiq-secondary"
      }`}
    >
      <Image
        src={imgSrc}
        alt=""
        aria-hidden="true"
        fill
        loading="lazy"
        sizes="(min-width: 1024px) 50vw, 100vw"
        className="scale-105 object-cover blur-[6px]"
        onError={() => setImgSrc(fallbackImage)}
      />

      {isRent ? (
        <>
          <div className="absolute inset-0 bg-black/80 dark:bg-black/85" />
          <div className="absolute inset-0 bg-gradient-to-t from-softiq-secondary/55 via-transparent to-softiq-primary/20 dark:from-softiq-secondary/45 dark:to-softiq-primary/15" />
        </>
      ) : (
        <div className="absolute inset-0 bg-black/75" />
      )}

      <div className="absolute inset-0 flex flex-col justify-between p-7 text-white">
        <div className="flex justify-end">
          <span
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold tracking-wide backdrop-blur-md ${
              isRent
                ? "border-white/15 bg-black/30 text-white/70 dark:bg-black/45"
                : "border-white/15 bg-white/10 text-white/70"
            }`}
          >
            {titles.week}
          </span>
        </div>

        <div className="mt-auto">
          <h3 className="max-w-xl text-4xl font-black leading-[0.95] tracking-tight drop-shadow-xl sm:text-5xl xl:text-6xl">
            {title}
          </h3>

          {isRent && (
            <div className="mt-3">
              <span className="inline-flex rounded-full border border-softiq-secondary/40 bg-softiq-secondary px-4 py-2 text-base font-black leading-none tracking-wide text-softiq-primary shadow-lg shadow-black/20 backdrop-blur-md sm:text-lg">
                {titles.rentQualifier}
              </span>
            </div>
          )}

          <div className="mt-3 text-xs font-medium uppercase tracking-wide text-white/40">
            {titles.note}
          </div>

          <p className="mt-4 text-lg font-semibold text-white/90 drop-shadow-md sm:text-xl">
            {question}
          </p>

          <div className="mt-7 flex items-center justify-end gap-2 text-sm font-bold uppercase tracking-[0.18em] text-softiq-secondary-shade-200">
            {titles.prompt}
            <ChevronRightIcon className="size-5" />
          </div>

          <div className="mx-auto w-fit max-w-[160px] pt-14 text-center opacity-50">
            <span className="block text-xs font-bold uppercase tracking-[0.22em] text-softiq-secondary-shade-200">
              SOFTIQ.cz
            </span>
            <span className="block text-[10px] font-medium normal-case tracking-normal">
              hlídací pes na nemovitosti
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
